import { useState, useMemo } from "react";

// Tri-state sortable table hook (None → Asc → Desc → None).
// `sortKey === null` means unsorted (original order preserved).
export function useSortableTable(data, defaultKey = null, defaultDir = "asc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const toggleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
      return;
    }
    if (sortDir === "asc") {
      setSortDir("desc");
      return;
    }
    // Currently descending → cycle back to unsorted.
    setSortKey(null);
    setSortDir("asc");
  };

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    const isEmpty = (v) => v === null || v === undefined || v === "";
    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      // Null / empty values always sort last, regardless of direction,
      // so switching Asc↔Desc doesn't shuffle them between top and bottom.
      const aEmpty = isEmpty(aVal);
      const bEmpty = isEmpty(bVal);
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortDir === "asc" ? -1 : 1;
      if (aStr > bStr) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggleSort };
}
